import React, { FC } from 'react';
import styled from 'styled-components';
import Checkbox from '@minecraft.minusable-checkbox';
import UncontrolledArrow from '@minecraft.uncontrolled-arrow';
import { SelectOption } from '@minecraft.typings';
import { helpers } from '@minecraft.utils';
import { CheckboxListWithSearchProps } from '../shared/types';

const { getAllNestedOptions } = helpers;

const StyledCheckboxItem = styled.div<{
  $isItemSelected?: boolean;
  $isItemDisabled?: boolean;
  $nestingLevel: number;
}>`
  border: 0;
  font-size: 1rem;
  text-align: inherit;
  width: 100%;

  ${(props) =>
    !props.$isItemDisabled &&
    `
  cursor: pointer;
  `}

  ${(props) =>
    props.$nestingLevel === 1 &&
    `
    font-weight: 600;
  `}
  ${(props) =>
    props.$nestingLevel === 2 &&
    `
    font-weight: 500;
  `}
`;

const CheckboxItemInner = styled.div`
  align-items: center;
  display: flex;
  padding: 0.5rem 0;
  justify-content: space-between;
`;

const StyledCheckbox = styled(Checkbox)`
  margin: 0;
  font-size: 1rem;
  display: flex;
  align-items: center;
`;

const StyledUncontrolledArrow = styled(UncontrolledArrow)`
  margin-left: 1rem;
  height: 1.375rem;
  width: 1.375rem;
`;

interface Props extends Partial<CheckboxListWithSearchProps> {
  isItemSelected: boolean;
  isItemCountExist: boolean;
  isItemDisabled: boolean;
  areSubOptionsExist: boolean;
  checkboxEnable: boolean;
  minusEnable: boolean;
  nestingLevel: number;
  item: SelectOption;
  toggleParentOptions: (parentItem: SelectOption) => void;
  onCheckboxItemSelect: (newItems: SelectOption[]) => void;
  className?: string;
}

const CheckboxItem: FC<Props> = ({
  isItemSelected,
  isItemCountExist,
  isItemDisabled,
  areSubOptionsExist,
  checkboxEnable,
  nestingLevel,
  minusEnable,
  item,
  toggleParentOptions,
  onCheckboxItemSelect,
  className,
}) => {
  const renderLabel = (): JSX.Element => {
    return (
      <span>
        {item.label}
        {isItemCountExist ? ` (${item.count})` : ''}
      </span>
    );
  };

  return (
    <StyledCheckboxItem
      $isItemDisabled={isItemDisabled}
      $nestingLevel={item?.options && nestingLevel}
      onClick={(event): void => {
        if (isItemDisabled || !checkboxEnable) return;

        const newItems = areSubOptionsExist ? getAllNestedOptions(item.options) : [item];

        event.preventDefault();

        onCheckboxItemSelect(newItems);
      }}
      className={className}
    >
      <CheckboxItemInner>
        {checkboxEnable ? (
          <StyledCheckbox
            isChecked={isItemSelected}
            minusEnable={minusEnable}
            disabled={isItemDisabled}
            label={item.label}
            count={item.count}
            readOnly
          />
        ) : (
          renderLabel()
        )}
        {areSubOptionsExist && nestingLevel === 1 && (
          <StyledUncontrolledArrow onClick={(): void => toggleParentOptions(item)} />
        )}
      </CheckboxItemInner>
    </StyledCheckboxItem>
  );
};

export default CheckboxItem;
